import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import EntriesTab from "@/components/project-detail/EntriesTab";
import PeopleTab from "@/components/project-detail/PeopleTab";
import ProgressTab from "@/components/project-detail/ProgressTab";
import CheckPaymentsTab from "@/components/project-detail/CheckPaymentsTab";
import SitePhotosTab from "@/components/project-detail/SitePhotosTab";
import BottomNavigation from "@/components/BottomNavigation";
import { AlertCircle, Crown, LockIcon, Loader2, CalendarClock, RefreshCw, LogOut, MoreVertical, Plus, Search, SlidersHorizontal, Info } from "lucide-react";
import { Alert, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { projectsAPI, entriesAPI } from "@/api";
import useSubscription from "@/contexts/SubscriptionContext";
import { Project } from "@/types";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { useAuth } from "@/contexts/AuthContext";
import { useUserPreferences } from "@/contexts/UserPreferencesContext";
import CheckStatusDialog from "@/components/CheckStatusDialog";
import { supabase } from "@/integrations/supabase/client";
import { useProjectRole } from "@/hooks/useProjectRole";
import { useTranslation } from "react-i18next";
import { ScreenHeader } from "@/components/redesign/ScreenHeader";
import { SummaryCard, SummaryStat } from "@/components/redesign/SummaryCard";
import { MonoValue } from "@/components/redesign/MonoValue";
import { StatusBadge } from "@/components/redesign/StatusBadge";
import { cn } from "@/lib/utils";
import { Switch } from "@/components/ui/switch";

// Helper function to map database project format to the Project type
const mapDatabaseProjectToProjectType = (dbProject: any): Project => {
  return {
    id: dbProject.id,
    name: dbProject.name,
    status: dbProject.status || "active",
    progress: dbProject.progress || 0,
    costs: {
      try: 0,
      usd: 0,
      eur: 0,
    },
    details: {
      parcel: dbProject.details?.parcel || "",
      lot: dbProject.details?.lot || "",
      flats: dbProject.details?.flats || 0,
      shops: dbProject.details?.shops || 0,
      area: dbProject.details?.area || 0,
      costPerSquareMeter: dbProject.details?.costPerSquareMeter || 0,
      units: dbProject.details?.units || []
    },
    ...(dbProject.units && { units: dbProject.units })
  };
};

const formatCurrencyValue = (value: number, currency: string): string => {
  if (!value) return "0";
  let decimalPlaces = 2;
  if (currency === 'XAU') decimalPlaces = 4;
  if (currency === 'BTC') decimalPlaces = 6;
  return value.toLocaleString('tr-TR', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces
  });
};

const getCurrencyPrefix = (currency: string): string => {
  switch (currency) {
    case 'USD': return '$';
    case 'EUR': return '€';
    case 'BTC': return '₿';
    case 'TRY': return '₺';
    default: return '';
  }
};

const getCurrencySuffix = (currency: string): string => {
  switch (currency) {
    case 'XAU': return ' XAU';
    case 'GBP': return ' GBP';
    case 'JPY': return ' JPY';
    case 'RUB': return ' RUB';
    case 'KWD': return ' KWD';
    case 'SAR': return ' SAR';
    case 'CHF': return ' CHF';
    default: return '';
  }
};

const ProjectDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { status } = useSubscription();
  const { planType, trialDaysRemaining } = status;
  const { user } = useAuth();
  const { preferences } = useUserPreferences();
  const { t } = useTranslation();

  const permissions = useProjectRole(id || '');

  const [activeTab, setActiveTab] = useState("entries");
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const isTrialExpired = planType === 'expired';

  const [project, setProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detailedCosts, setDetailedCosts] = useState<any>(null);
  const [costsLoading, setCostsLoading] = useState(false);
  // Project-wide advance & açık hesap ledger totals + which metric the summary shows
  const [ledgerTotals, setLedgerTotals] = useState<{ advance: Record<string, number>; openAccount: Record<string, number> } | null>(null);
  const [financialView, setFinancialView] = useState<'cost' | 'advance' | 'open_account'>('cost');
  const [upcomingChecks, setUpcomingChecks] = useState<any[]>([]);
  const [checkStatusDialogOpen, setCheckStatusDialogOpen] = useState(false);
  const [selectedCheck, setSelectedCheck] = useState<any | null>(null);
  const [soldUnits, setSoldUnits] = useState<Array<{projectId: string, unitType: string, unitNumber: number}>>([]);
  const [saleEntries, setSaleEntries] = useState<Array<{unitType: string, unitNumber: number, buyerName?: string, description?: string, buyerPhone?: string}>>([]);
  const [showOnlyOfficial, setShowOnlyOfficial] = useState(false);
  const [financialSearch, setFinancialSearch] = useState("");

  // Load project-wide advance & açık hesap ledger totals (refreshes with project data)
  useEffect(() => {
    if (!id) return;
    let active = true;
    const load = () => {
      entriesAPI
        .getProjectLedgerTotals(id)
        .then((totals) => { if (active) setLedgerTotals(totals); })
        .catch(() => {});
    };
    load();
    window.addEventListener('project-data-refresh', load);
    return () => {
      active = false;
      window.removeEventListener('project-data-refresh', load);
    };
  }, [id]);

  useEffect(() => {
    if (isTrialExpired && !redirecting) {
      setRedirecting(true);
      const timer = setTimeout(() => {
        navigate("/subscription-expired", {
          state: {
            message: "Aboneliğinizin süresi doldu. Projelere erişmek için bir abonelik planı seçin.",
            returnPath: `/project/${id}`
          }
        });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isTrialExpired, redirecting, navigate, id]);

  useEffect(() => {
    const navState = (location.state as { activeTab?: string } | null) ?? null;
    if (!navState?.activeTab) return;

    if (navState.activeTab) {
      setActiveTab(navState.activeTab === "materials" ? "entries" : navState.activeTab);
    }

    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, navigate]);

  const fetchProjectData = async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const projectData = await projectsAPI.getProject(id);
      if (!projectData) throw new Error("Project not found");
      const mappedProject = mapDatabaseProjectToProjectType(projectData);
      setProject(mappedProject);
    } catch (err) {
      setError(t("projects.error_loading_project"));
      toast.error(t("projects.error_loading_project"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProjectData();
    const handleProjectDataRefresh = (event: any) => {
      const { projectId } = event.detail;
      if (projectId === id) {
        fetchProjectData();
        fetchSoldUnits();
      }
    };
    window.addEventListener('projectDataChanged', handleProjectDataRefresh);
    return () => window.removeEventListener('projectDataChanged', handleProjectDataRefresh);
  }, [id]);

  useEffect(() => {
    const loadDetailedCosts = async () => {
      if (costsLoading) return;
      setCostsLoading(true);
      try {
        const costs = await projectsAPI.getProjectCostsFromEntries(id);
        const preferredCurrencies = [
          preferences?.base_currency || "TRY",
          preferences?.second_currency,
          preferences?.third_currency
        ].filter(Boolean);

        const formattedCosts = {
          official: {
            TRY: costs.officialCosts || 0,
            USD: costs.officialCosts_usd || 0,
            EUR: costs.officialCosts_eur || 0,
            GBP: costs.officialCosts_gbp || 0,
            JPY: costs.officialCosts_jpy || 0,
            RUB: costs.officialCosts_rub || 0,
            KWD: costs.officialCosts_kwd || 0,
            SAR: costs.officialCosts_sar || 0,
            CHF: costs.officialCosts_chf || 0,
            XAU: costs.officialCosts_xau || 0,
            BTC: costs.officialCosts_btc || 0
          },
          unofficial: {
            TRY: costs.unofficialCosts || 0,
            USD: costs.unofficialCosts_usd || 0,
            EUR: costs.unofficialCosts_eur || 0,
            GBP: costs.unofficialCosts_gbp || 0,
            JPY: costs.unofficialCosts_jpy || 0,
            RUB: costs.unofficialCosts_rub || 0,
            KWD: costs.unofficialCosts_kwd || 0,
            SAR: costs.unofficialCosts_sar || 0,
            CHF: costs.unofficialCosts_chf || 0,
            XAU: costs.unofficialCosts_xau || 0,
            BTC: costs.unofficialCosts_btc || 0
          },
          total: {
            TRY: costs.totalCosts_try || 0,
            USD: costs.totalCosts_usd || 0,
            EUR: costs.totalCosts_eur || 0,
            GBP: costs.totalCosts_gbp || 0,
            JPY: costs.totalCosts_jpy || 0,
            RUB: costs.totalCosts_rub || 0,
            KWD: costs.totalCosts_kwd || 0,
            SAR: costs.totalCosts_sar || 0,
            CHF: costs.totalCosts_chf || 0,
            XAU: costs.totalCosts_xau || 0,
            BTC: costs.totalCosts_btc || 0
          }
        };

        if (costs.detailedCosts) {
          const detailedCostsRaw = costs.detailedCosts;
          preferredCurrencies.forEach(currency => {
            if (currency && !["TRY", "USD", "EUR"].includes(currency)) {
              const currencyData = detailedCostsRaw[currency];
              if (currencyData) {
                formattedCosts.official[currency] = currencyData.official || 0;
                formattedCosts.unofficial[currency] = currencyData.unofficial || 0;
                formattedCosts.total[currency] = currencyData.total || 0;
              } else {
                formattedCosts.official[currency] = 0;
                formattedCosts.unofficial[currency] = 0;
                formattedCosts.total[currency] = 0;
              }
            }
          });
        }

        setDetailedCosts(formattedCosts);

        if (project) {
          setProject(prev => {
            if (!prev) return null;
            return {
              ...prev,
              costs: {
                try: costs.totalCosts_try || 0,
                usd: costs.totalCosts_usd || 0,
                eur: costs.totalCosts_eur || 0,
                gbp: costs.totalCosts_gbp || 0,
                jpy: costs.totalCosts_jpy || 0,
                rub: costs.totalCosts_rub || 0,
                kwd: costs.totalCosts_kwd || 0,
                sar: costs.totalCosts_sar || 0,
                chf: costs.totalCosts_chf || 0,
                xau: costs.totalCosts_xau || 0,
                btc: costs.totalCosts_btc || 0
              }
            };
          });
        }
      } catch (error) {
        // silently ignore
      } finally {
        setCostsLoading(false);
      }
    };

    loadDetailedCosts();

    const handleProjectDataRefresh = (event: any) => {
      const { projectId } = event.detail;
      if (projectId === id) {
        loadDetailedCosts();
        fetchProjectData();
        fetchSoldUnits();
      }
    };

    window.addEventListener('project-data-refresh', handleProjectDataRefresh);
    return () => window.removeEventListener('project-data-refresh', handleProjectDataRefresh);
  }, [id, project, preferences]);

  useEffect(() => {
    const loadUpcomingChecks = async () => {
      if (!id || !project) return;
      try {
        const checksData = await projectsAPI.getProjectUpcomingCheckPayments(id);
        setUpcomingChecks(checksData);
      } catch (error) {
        toast.error(t('projects.detail.checks_load_error'));
      }
    };
    loadUpcomingChecks();
  }, [id, project]);

  const fetchSoldUnits = async () => {
    if (!id || !user?.id) return;
    try {
      const { data, error } = await supabase
        .from('entries')
        .select('description, project_id, metric_details')
        .eq('type', 'sale')
        .eq('project_id', id)
        .eq('user_id', user.id);
      if (error) throw error;

      const soldUnitsData = [];
      const saleEntriesData = [];

      data.forEach(entry => {
        const flatMatchTurkish = entry.description.match(/Daire (\d+) Satışı/);
        const shopMatchTurkish = entry.description.match(/Dükkan (\d+) Satışı/);
        const flatMatchTranslated = entry.description.match(/Flat (\d+) Sale/);
        const shopMatchTranslated = entry.description.match(/Shop (\d+) Sale/);

        let unitData = null;
        if (flatMatchTurkish || flatMatchTranslated) {
          const match = flatMatchTurkish || flatMatchTranslated;
          unitData = { projectId: entry.project_id, unitType: 'flat', unitNumber: parseInt(match[1]) };
        } else if (shopMatchTurkish || shopMatchTranslated) {
          const match = shopMatchTurkish || shopMatchTranslated;
          unitData = { projectId: entry.project_id, unitType: 'shop', unitNumber: parseInt(match[1]) };
        }

        if (unitData) {
          soldUnitsData.push(unitData);
          let buyerDetails = { unitType: unitData.unitType, unitNumber: unitData.unitNumber, buyerName: '', description: '', buyerPhone: '' };
          if (entry.metric_details) {
            try {
              const metricData = typeof entry.metric_details === 'string' ? JSON.parse(entry.metric_details) : entry.metric_details;
              if (metricData.buyerName) buyerDetails.buyerName = metricData.buyerName;
              if (metricData.customDescription) buyerDetails.description = metricData.customDescription;
              if (metricData.buyerPhone) buyerDetails.buyerPhone = metricData.buyerPhone;
            } catch (e) { /* ignore */ }
          }
          saleEntriesData.push(buyerDetails);
        }
      });

      setSoldUnits(soldUnitsData);
      setSaleEntries(saleEntriesData);
    } catch (error) { /* ignore */ }
  };

  useEffect(() => {
    fetchSoldUnits();
  }, [id, user?.id]);

  const isUnitSold = (unitType: 'flat' | 'shop', unitNumber: number) => {
    return soldUnits.some(soldUnit =>
      soldUnit.projectId === id &&
      soldUnit.unitType === unitType &&
      soldUnit.unitNumber === unitNumber
    );
  };

  if (isTrialExpired) {
    return (
      <div className="page-container">
        <ScreenHeader
          title={t("projects.detail.project_detail")}
          backLabel="Anasayfa"
          onBack={() => navigate("/")}
        />
        <main className="main-content bg-gray-25">
          <div className="px-4 pb-4 flex flex-col items-center justify-center py-12">
            <div className="text-center max-w-md">
              <div className="p-4 rounded-full bg-error-50 inline-block mb-4">
                <LockIcon className="h-12 w-12 text-error-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2 text-gray-900">{t("projects.detail.cannot_access")}</h2>
              <p className="text-gray-600 mb-6">{t("projects.detail.trial_expired_message")}</p>
              <Button size="lg" onClick={() => navigate("/subscription")} className="mb-4 bg-brand-600 hover:bg-brand-700">
                <Crown className="mr-2 h-4 w-4" />
                {t("projects.detail.subscription_plans")}
              </Button>
              <p className="text-sm text-gray-500">{t("projects.detail.redirecting_message")}</p>
            </div>
          </div>
        </main>
        <BottomNavigation />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="page-container">
        <ScreenHeader
          title={t("projects.detail.loading_project_title")}
          backLabel="Anasayfa"
          onBack={() => navigate("/")}
        />
        <main className="main-content bg-gray-25">
          <div className="px-4 pb-4 flex items-center justify-center h-full">
            <div className="text-center">
              <Loader2 className="h-10 w-10 animate-spin mx-auto mb-4 text-brand-600" />
              <p className="text-lg font-medium text-gray-700">{t("projects.detail.project_loading")}</p>
            </div>
          </div>
        </main>
        <BottomNavigation />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="page-container">
        <ScreenHeader
          title={t("common.error")}
          backLabel="Anasayfa"
          onBack={() => navigate("/")}
        />
        <main className="main-content bg-gray-25">
          <div className="px-4 py-4">
            <div className="bg-error-50 border border-error-100 rounded-xl p-4 mb-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-error-600 shrink-0 mt-0.5" />
              <p className="text-error-600 text-sm">{error || t("projects.project_not_found")}</p>
            </div>
            <Button variant="outline" className="w-full" onClick={() => navigate(-1)}>
              {t("common.back")}
            </Button>
          </div>
        </main>
        <BottomNavigation />
      </div>
    );
  }

  const baseCurrency = preferences?.base_currency || "TRY";
  const totalArea = project.details?.area || 0;

  let baseCurrencyTotal = 0;
  if (detailedCosts && detailedCosts.total) {
    baseCurrencyTotal = detailedCosts.total[baseCurrency] || 0;
  } else {
    baseCurrencyTotal = baseCurrency === "TRY" ? project.costs.try :
      baseCurrency === "USD" ? project.costs.usd :
        baseCurrency === "EUR" ? project.costs.eur :
          project.costs[baseCurrency.toLowerCase()] || 0;
  }

  const costPerM2 = totalArea > 0 ? baseCurrencyTotal / totalArea : 0;

  // Financial summary metric switch (cost / advance / açık hesap)
  const advanceTotal = ledgerTotals?.advance?.[baseCurrency] || 0;
  const openAccountTotal = ledgerTotals?.openAccount?.[baseCurrency] || 0;
  const financialDisplayValue =
    financialView === 'advance' ? advanceTotal :
    financialView === 'open_account' ? openAccountTotal :
    baseCurrencyTotal;

  const handleTabChange = (value: string) => setActiveTab(value);

  const handleCompleteProject = async () => {
    if (!id) return;
    setIsCompleting(true);
    try {
      const data = await projectsAPI.completeProject(id);
      if (!data) throw new Error("Failed to complete project");
      setProject(prev => prev ? { ...prev, status: 'completed' } : null);
      toast.success(t("projects.detail.complete_project.success_message"));
    } catch (error) {
      toast.error(t("projects.detail.complete_project.error_message"));
    } finally {
      setIsCompleting(false);
      setCompleteDialogOpen(false);
    }
  };

  const handleLeaveProject = async () => {
    if (!id) return;
    setIsLeaving(true);
    try {
      await projectsAPI.leaveProject(id);
      toast.success(t("projects.detail.leave_project.success_message"));
      navigate('/');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("projects.detail.leave_project.error_message"));
    } finally {
      setIsLeaving(false);
      setLeaveDialogOpen(false);
    }
  };

  const handleCheckStatusClick = (check: any) => {
    setSelectedCheck(check);
    setCheckStatusDialogOpen(true);
  };

  const handleCheckStatusUpdate = async (status: 'alindi' | 'ertelendi' | 'alinamadi', newDueDate?: Date) => {
    if (!selectedCheck || !user?.id) return;
    try {
      await entriesAPI.updateCheckStatus(selectedCheck.id, status, newDueDate, user.id);
      let message = "";
      if (status === 'alindi') {
        message = selectedCheck.isGivenCheck ? t("projects.check_paid") : t("projects.check_collected");
      } else if (status === 'ertelendi') {
        message = t("projects.check_postponed");
      } else {
        message = selectedCheck.isGivenCheck ? t("projects.check_not_paid") : t("projects.check_not_collected");
      }
      toast.success(message);
      const checksData = await projectsAPI.getProjectUpcomingCheckPayments(id as string);
      setUpcomingChecks(checksData);
    } catch (error) {
      toast.error(t("projects.detail.check_status_update_error"));
    } finally {
      setCheckStatusDialogOpen(false);
      setSelectedCheck(null);
    }
  };

  const refreshCosts = () => {
    setCostsLoading(true);
    const loadCosts = async () => {
      try {
        const costs = await projectsAPI.getProjectCostsFromEntries(id);
        const preferredCurrencies = [
          preferences?.base_currency || "TRY",
          preferences?.second_currency,
          preferences?.third_currency
        ].filter(Boolean);

        const formattedCosts = {
          official: {
            TRY: costs.officialCosts || 0,
            USD: costs.officialCosts_usd || 0,
            EUR: costs.officialCosts_eur || 0,
            GBP: costs.officialCosts_gbp || 0,
            JPY: costs.officialCosts_jpy || 0,
            RUB: costs.officialCosts_rub || 0,
            KWD: costs.officialCosts_kwd || 0,
            SAR: costs.officialCosts_sar || 0,
            CHF: costs.officialCosts_chf || 0,
            XAU: costs.officialCosts_xau || 0,
            BTC: costs.officialCosts_btc || 0
          },
          unofficial: {
            TRY: costs.unofficialCosts || 0,
            USD: costs.unofficialCosts_usd || 0,
            EUR: costs.unofficialCosts_eur || 0,
            GBP: costs.unofficialCosts_gbp || 0,
            JPY: costs.unofficialCosts_jpy || 0,
            RUB: costs.unofficialCosts_rub || 0,
            KWD: costs.unofficialCosts_kwd || 0,
            SAR: costs.unofficialCosts_sar || 0,
            CHF: costs.unofficialCosts_chf || 0,
            XAU: costs.unofficialCosts_xau || 0,
            BTC: costs.unofficialCosts_btc || 0
          },
          total: {
            TRY: costs.totalCosts_try || 0,
            USD: costs.totalCosts_usd || 0,
            EUR: costs.totalCosts_eur || 0,
            GBP: costs.totalCosts_gbp || 0,
            JPY: costs.totalCosts_jpy || 0,
            RUB: costs.totalCosts_rub || 0,
            KWD: costs.totalCosts_kwd || 0,
            SAR: costs.totalCosts_sar || 0,
            CHF: costs.totalCosts_chf || 0,
            XAU: costs.totalCosts_xau || 0,
            BTC: costs.totalCosts_btc || 0
          }
        };

        if (costs.detailedCosts) {
          const detailedCostsRaw = costs.detailedCosts;
          preferredCurrencies.forEach(currency => {
            if (currency && !["TRY", "USD", "EUR"].includes(currency)) {
              const currencyData = detailedCostsRaw[currency];
              if (currencyData) {
                formattedCosts.official[currency] = currencyData.official || 0;
                formattedCosts.unofficial[currency] = currencyData.unofficial || 0;
                formattedCosts.total[currency] = currencyData.total || 0;
              } else {
                formattedCosts.official[currency] = 0;
                formattedCosts.unofficial[currency] = 0;
                formattedCosts.total[currency] = 0;
              }
            }
          });
        }

        setDetailedCosts(formattedCosts);
        if (project) {
          setProject(prev => {
            if (!prev) return null;
            return {
              ...prev,
              costs: {
                try: costs.totalCosts_try || 0,
                usd: costs.totalCosts_usd || 0,
                eur: costs.totalCosts_eur || 0,
                gbp: costs.totalCosts_gbp || 0,
                jpy: costs.totalCosts_jpy || 0,
                rub: costs.totalCosts_rub || 0,
                kwd: costs.totalCosts_kwd || 0,
                sar: costs.totalCosts_sar || 0,
                chf: costs.totalCosts_chf || 0,
                xau: costs.totalCosts_xau || 0,
                btc: costs.totalCosts_btc || 0
              }
            };
          });
        }
        toast.success(t("projects.detail.costs_updated"));
      } catch (error) {
        toast.error(t('projects.detail.costs_refresh_error'));
      } finally {
        setCostsLoading(false);
      }
    };
    loadCosts();
  };

  const baseCurrencyPrefix = getCurrencyPrefix(baseCurrency);
  const baseCurrencySuffix = getCurrencySuffix(baseCurrency);

  return (
    <div className="page-container bg-gray-25">
      {/* Navy screen header */}
      <ScreenHeader
        title={project.name}
        backLabel="Anasayfa"
        onBack={() => navigate("/")}
        right={
          <>
            <button
              type="button"
              aria-label="Daha fazla"
              className="text-brand-25 hover:text-white transition-colors"
            >
              <MoreVertical className="h-5 w-5" />
            </button>
          </>
        }
      />

      {/* Navy summary card */}
      <SummaryCard className="mx-auto w-full max-w-[390px] rounded-b-[24px] px-4 pb-[22px] pt-3 shadow-[0px_4px_4px_rgba(16,24,40,0.1),0px_2px_2px_rgba(16,24,40,0.06)] sm:px-5">
        {/* Role badge + edit row */}
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 self-end sm:self-auto">
            {permissions.role && (
              <StatusBadge
                tone={
                  permissions.role === 'owner' ? 'brand' :
                    permissions.role === 'editor' ? 'success' : 'neutral'
                }
              >
                {permissions.role === 'owner' ? t('projects.detail.role_owner') :
                  permissions.role === 'editor' ? t('projects.detail.role_editor') :
                    t('projects.detail.role_viewer')}
              </StatusBadge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={refreshCosts}
              aria-label="Yenile"
              className="text-brand-25 hover:text-white transition-colors"
            >
              <RefreshCw className={cn("h-4 w-4", costsLoading && "animate-spin")} />
            </button>
            {permissions.canEdit && (
              <Button
                variant="secondary"
                size="sm"
                className="h-8 rounded-full border border-brand-300 bg-brand-800 text-brand-25 hover:bg-brand-700 hover:text-white text-[13px] font-medium"
                onClick={() => navigate(`/new-entry?projectId=${id}`)}
              >
                <Plus className="mr-1 h-3.5 w-3.5" />
                İşlem Ekle
              </Button>
            )}
          </div>
        </div>

        {/* Big total value + metric switch */}
        <div className="mb-3">
          <div className="mb-1 flex items-center gap-2">
            <select
              value={financialView}
              onChange={(e) => setFinancialView(e.target.value as 'cost' | 'advance' | 'open_account')}
              className="rounded-md border border-brand-700 bg-brand-800 px-2 py-1 text-[11px] font-bold uppercase tracking-[0.04em] text-brand-25 focus:outline-none"
            >
              <option value="cost">{t("projects.total_cost")}</option>
              <option value="advance">Avans (Anlaşma/Bağlantı)</option>
              <option value="open_account">Açık Hesap</option>
            </select>
            <span className="text-[11px] font-bold uppercase tracking-[0.04em] text-brand-200">· {baseCurrency}</span>
          </div>
          {costsLoading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin text-white/60" />
              <span className="text-white/60 text-sm">{t("projects.detail.costs_loading")}</span>
            </div>
          ) : (
            <MonoValue
              value={financialDisplayValue}
              prefix={baseCurrencyPrefix}
              suffix={baseCurrencySuffix || undefined}
              format
              fractionDigits={baseCurrency === 'XAU' ? 4 : baseCurrency === 'BTC' ? 6 : 2}
              tone="white"
              size="xl"
            />
          )}
          {financialView === 'advance' && (
            <p className="mt-1 text-[11px] text-brand-200">Kişilere peşin ödenen, henüz faturalanmamış toplam tutar.</p>
          )}
          {financialView === 'open_account' && (
            <p className="mt-1 text-[11px] text-brand-200">Kişilere ödenecek (henüz maliyete eklenmemiş) toplam borç.</p>
          )}
        </div>

        {/* Currency sub-tiles */}
        <div className="flex gap-2 flex-wrap mb-3">
          {/* Base currency chip */}
          <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-[11px] font-bold text-white">
            {baseCurrency}
          </span>
          {preferences?.second_currency && preferences.second_currency !== baseCurrency && (
            <span className="rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-medium text-brand-100">
              {preferences.second_currency}
            </span>
          )}
          {preferences?.third_currency &&
            preferences.third_currency !== baseCurrency &&
            preferences.third_currency !== preferences?.second_currency && (
              <span className="rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-medium text-brand-100">
                {preferences.third_currency}
              </span>
            )}
        </div>

        {/* Official / unofficial stat tiles */}
        {financialView === 'cost' && detailedCosts && preferences?.base_official_divide && (
          <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <SummaryStat>
              <p className="text-[10px] font-bold uppercase tracking-[0.04em] text-success-400 mb-1">
                {t("projects.official_cost")}
              </p>
              <MonoValue
                value={detailedCosts.official[baseCurrency] || 0}
                prefix={baseCurrencyPrefix}
                suffix={baseCurrencySuffix || undefined}
                format
                fractionDigits={baseCurrency === 'XAU' ? 4 : baseCurrency === 'BTC' ? 6 : 2}
                tone="white"
                size="sm"
              />
            </SummaryStat>
            <SummaryStat>
              <p className="text-[10px] font-bold uppercase tracking-[0.04em] text-warning-300 mb-1">
                {t("projects.unofficial_cost")}
              </p>
              <MonoValue
                value={detailedCosts.unofficial[baseCurrency] || 0}
                prefix={baseCurrencyPrefix}
                suffix={baseCurrencySuffix || undefined}
                format
                fractionDigits={baseCurrency === 'XAU' ? 4 : baseCurrency === 'BTC' ? 6 : 2}
                tone="white"
                size="sm"
              />
            </SummaryStat>
          </div>
        )}

        {/* Second currency tile */}
        {financialView === 'cost' && detailedCosts && preferences?.second_currency && preferences.second_currency !== baseCurrency && (
          <div className="mb-2">
            <SummaryStat>
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-bold uppercase tracking-[0.04em] text-brand-200">
                  {preferences.second_currency}
                </p>
                <MonoValue
                  value={detailedCosts.total[preferences.second_currency] || 0}
                  prefix={getCurrencyPrefix(preferences.second_currency)}
                  suffix={getCurrencySuffix(preferences.second_currency) || undefined}
                  format
                  fractionDigits={preferences.second_currency === 'XAU' ? 4 : preferences.second_currency === 'BTC' ? 6 : 2}
                  tone="white"
                  size="sm"
                />
              </div>
            </SummaryStat>
          </div>
        )}

        {/* Third currency tile */}
        {financialView === 'cost' && detailedCosts && preferences?.third_currency &&
          preferences.third_currency !== baseCurrency &&
          preferences.third_currency !== preferences?.second_currency && (
            <div className="mb-2">
              <SummaryStat>
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-bold uppercase tracking-[0.04em] text-brand-200">
                    {preferences.third_currency}
                  </p>
                  <MonoValue
                    value={detailedCosts.total[preferences.third_currency] || 0}
                    prefix={getCurrencyPrefix(preferences.third_currency)}
                    suffix={getCurrencySuffix(preferences.third_currency) || undefined}
                    format
                    fractionDigits={preferences.third_currency === 'XAU' ? 4 : preferences.third_currency === 'BTC' ? 6 : 2}
                    tone="white"
                    size="sm"
                  />
                </div>
              </SummaryStat>
            </div>
          )}

        {/* Progress bar */}
        <div className="mt-3">
          <div className="mb-1 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-[11px] text-brand-200 font-medium uppercase tracking-[0.04em]">
              {t("projects.detail.progress_completed", { progress: project.progress })}
            </span>
            <MonoValue value={project.progress} suffix="%" tone="white" size="meta" />
          </div>
          <div className="w-full h-1.5 rounded-full bg-white/20">
            <div
              className="h-1.5 rounded-full bg-brand-300 transition-all"
              style={{ width: `${project.progress}%` }}
            />
          </div>
        </div>
      </SummaryCard>

      <main className="main-content bg-gray-25">
        <div className="mx-auto w-full max-w-[390px] pb-4">
          {/* Trial warning */}
          {planType === 'trial' && trialDaysRemaining <= 3 && (
            <div className="mx-4 mt-4 bg-warning-50 border border-warning-300 rounded-xl p-4 flex items-center gap-3">
              <Crown className="h-5 w-5 text-warning-600 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-warning-600">{t("projects.detail.trial_ending_warning")}</p>
                <p className="text-xs text-warning-600/70">
                  {t("projects.detail.remaining_days", { days: trialDaysRemaining })}
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={() => navigate("/subscription")}
                className="border-warning-300 text-warning-600 hover:bg-warning-50 h-8">
                {t("projects.detail.subscribe")}
              </Button>
            </div>
          )}

          {/* Viewer restriction banner */}
          {permissions.role === 'viewer' && (
            <div className="mx-4 mt-4 bg-warning-50 border border-warning-300 rounded-xl p-4 flex items-start gap-3">
              <LockIcon className="h-4 w-4 text-warning-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-warning-600">{t("projects.detail.readonly_access_notice")}</p>
                <p className="text-xs text-warning-600/80 mt-0.5">{t("projects.detail.readonly_description")}</p>
              </div>
            </div>
          )}

          {/* Area & cost info */}
          {project.details && project.details.area > 0 && (
            <div className="mx-4 mt-4 bg-white border border-gray-200 rounded-2xl shadow-sm p-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.04em] text-brand-800 mb-2">
                {t("projects.unit_costs")}
              </p>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-gray-600">{t("projects.detail.total_area")}</span>
                <MonoValue value={totalArea} suffix="m²" tone="default" size="sm" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{t("projects.detail.cost_per_m2")}</span>
                <MonoValue
                  value={costPerM2}
                  prefix={baseCurrencyPrefix}
                  suffix={baseCurrencySuffix || "/m²"}
                  format
                  fractionDigits={2}
                  tone="brand"
                  size="sm"
                />
              </div>
            </div>
          )}

          {/* Upcoming checks alert */}
          {upcomingChecks.length > 0 && (
            <div className="mx-4 mt-4 bg-error-50 border border-error-100 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <CalendarClock className="h-4 w-4 text-error-600 shrink-0" />
                <span className="text-sm font-semibold text-error-600">
                  {t("projects.detail.upcoming_checks_alert")}
                </span>
                <span className="ml-auto text-[11px] font-bold bg-error-600 text-white rounded-full px-2 py-0.5">
                  {upcomingChecks.length}
                </span>
              </div>
              <div className="space-y-2">
                {upcomingChecks.slice(0, 2).map((check) => (
                  <div
                    key={check.id}
                    className="flex items-center justify-between bg-white rounded-lg p-2.5 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => navigate(`/entry/${check.id}`)}
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">{check.description}</p>
                      <div className="flex items-center gap-1 text-xs mt-0.5">
                        <span className={cn("font-semibold", check.dueInDays === 0 ? 'text-error-600' : 'text-warning-600')}>
                          {check.dueInDays === 0 ? t('projects.due_today') : t('projects.due_in_days', { days: check.dueInDays })}
                        </span>
                        <span className="text-gray-400">·</span>
                        <span className="text-gray-500">{format(new Date(check.dueDate), "d MMMM yyyy", { locale: tr })}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <MonoValue
                        value={check.amount}
                        prefix={check.currency === 'USD' ? '$' : check.currency === 'EUR' ? '€' : '₺'}
                        format
                        fractionDigits={2}
                        tone="error"
                        size="sm"
                      />
                      <p className="text-[10px] text-gray-500 mt-0.5">
                        {check.isGivenCheck ? t('projects.given_check') : t('projects.received_check')}
                      </p>
                    </div>
                  </div>
                ))}
                {upcomingChecks.length > 2 && (
                  <button
                    type="button"
                    className="w-full text-center text-xs font-medium text-error-600 hover:text-error-600/80 py-1"
                    onClick={() => setActiveTab("checks")}
                  >
                    {t("projects.detail.more_checks", { count: upcomingChecks.length - 2 })}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={handleTabChange} className="mt-4">
            <div className="px-5">
              <TabsList className="grid h-auto w-full grid-cols-5 gap-1 rounded-lg bg-[#ebf1ff] p-1 text-[13px]">
                <TabsTrigger value="entries" className="h-8 rounded-md border-0 px-3 text-sm font-semibold text-[#3b608f] data-[state=active]:bg-white data-[state=active]:font-bold data-[state=active]:text-brand-600 data-[state=active]:shadow-sm">
                  Finansal
                </TabsTrigger>
                <TabsTrigger value="people" className="h-8 rounded-md border-0 px-3 text-sm font-semibold text-[#3b608f] data-[state=active]:bg-white data-[state=active]:font-bold data-[state=active]:text-brand-600 data-[state=active]:shadow-sm">
                  Kişiler
                </TabsTrigger>
                <TabsTrigger value="site-photos" className="h-8 rounded-md border-0 px-3 text-sm font-semibold text-[#3b608f] data-[state=active]:bg-white data-[state=active]:font-bold data-[state=active]:text-brand-600 data-[state=active]:shadow-sm">
                  Belgeler
                </TabsTrigger>
                <TabsTrigger value="progress" className="h-8 rounded-md border-0 px-3 text-sm font-semibold text-[#3b608f] data-[state=active]:bg-white data-[state=active]:font-bold data-[state=active]:text-brand-600 data-[state=active]:shadow-sm">
                  İlerleme
                </TabsTrigger>
                <TabsTrigger value="irsaliyeler" className="h-8 rounded-md border-0 px-3 text-sm font-semibold text-[#3b608f] data-[state=active]:bg-white data-[state=active]:font-bold data-[state=active]:text-brand-600 data-[state=active]:shadow-sm">
                  İrsaliyeler
                </TabsTrigger>
              </TabsList>
            </div>

            {activeTab === "entries" && (
              <>
                <div className="mx-4 mt-4 sm:mx-5">
                  <h2 className="text-[18px] font-bold text-brand-900">Finansal kayıtlar</h2>
                  <p className="mt-1 text-[11px] leading-[16.5px] text-brand-800">
                    Projeye ait finansal hareketleri ara, filtrele ve durumlara göre takip et.
                  </p>
                  <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                    <div className="flex h-11 flex-1 items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 shadow-xs">
                      <Search className="h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Kayıtlarda ara..."
                        value={financialSearch}
                        onChange={(event) => setFinancialSearch(event.target.value)}
                        className="h-full w-full bg-transparent text-sm text-gray-700 placeholder:text-gray-400 outline-none"
                      />
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => setShowOnlyOfficial((prev) => !prev)}
                      className={cn(
                        "h-11 w-full rounded-xl border px-4 text-brand-900 transition-all duration-300 ease-out active:scale-[0.98] sm:w-auto",
                        showOnlyOfficial
                          ? "border-brand-200 bg-[linear-gradient(180deg,#EAF3FF_0%,#DCEBFF_100%)] text-brand-700 shadow-[0_10px_24px_rgba(21,101,192,0.18)]"
                          : "border-gray-200 bg-white text-gray-700 shadow-[0_1px_2px_rgba(16,24,40,0.08)] hover:bg-gray-50",
                      )}
                    >
                      <SlidersHorizontal
                        className={cn(
                          "h-4 w-4 transition-transform duration-300 ease-out",
                          showOnlyOfficial ? "rotate-6 text-brand-700" : "text-gray-600"
                        )}
                      />
                      <span className="ml-2 text-sm font-semibold">{showOnlyOfficial ? "Filtre aktif" : "Filtrele"}</span>
                    </Button>
                  </div>
                </div>
                <div className="mt-4 border-y border-gray-100 bg-white px-4 py-3 sm:px-5">
                  <div className="rounded-xl border border-error-100 bg-error-50 p-3 flex items-start gap-2 shadow-xs">
                    <Info className="h-4 w-4 text-warning-600 mt-0.5 shrink-0" />
                    <p className="text-xs text-warning-600">
                      Finansal görünümde resmi ve gayriresmi kayıtları ayrı ayrı analiz edebilirsiniz.
                    </p>
                  </div>
                  <div
                    className={cn(
                      "mt-4 flex flex-col gap-3 rounded-xl border px-4 py-3 transition-all duration-300 ease-out sm:flex-row sm:items-center sm:justify-between",
                      showOnlyOfficial
                        ? "border-brand-200 bg-[linear-gradient(180deg,#F4F9FF_0%,#EAF3FF_100%)] shadow-[0_10px_24px_rgba(21,101,192,0.12)]"
                        : "border-gray-300 bg-[#f2f6fa] shadow-[0_1px_2px_rgba(16,24,40,0.06)]"
                    )}
                  >
                    <div>
                      <p className="text-[13px] font-semibold text-gray-900">Sadece Resmi Kayıtları Göster</p>
                      <p className="text-xs text-gray-500">Listeyi sadece resmi kayıtlarla filtreler.</p>
                    </div>
                    <Switch
                      checked={showOnlyOfficial}
                      onCheckedChange={setShowOnlyOfficial}
                      className="shrink-0"
                    />
                  </div>
                </div>
              </>
            )}

            <div className="px-4 sm:px-5">
              <TabsContent value="entries" className="mt-4">
                {project && (
                  <EntriesTab
                    projectId={project.id}
                    projectStatus={project.status}
                    hideControls
                    searchQuery={financialSearch}
                    showOnlyOfficial={showOnlyOfficial}
                  />
                )}
              </TabsContent>

              <TabsContent value="people" className="mt-4">
                {project && <PeopleTab projectId={project.id} projectStatus={project.status} />}
              </TabsContent>

              <TabsContent value="progress" className="mt-4">
                {project && <ProgressTab projectId={project.id} projectStatus={project.status} />}
              </TabsContent>

              <TabsContent value="site-photos" className="mt-4">
                {project && <SitePhotosTab projectId={project.id} projectStatus={project.status} />}
              </TabsContent>

              <TabsContent value="checks" className="mt-4">
                {project && <CheckPaymentsTab projectId={project.id} projectStatus={project.status} />}
              </TabsContent>

              <TabsContent value="irsaliyeler" className="mt-4">
                {project && (
                  <EntriesTab
                    projectId={project.id}
                    projectStatus={project.status}
                    onlyEntryTypes={["delivery"]}
                  />
                )}
              </TabsContent>
            </div>
          </Tabs>

          {/* Detailed upcoming checks section */}
          {upcomingChecks.length > 0 && (
            <div className="mx-4 mt-6">
              <h3 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
                <CalendarClock className="h-5 w-5 text-error-600" />
                {t("projects.detail.upcoming_checks_detailed")}
              </h3>
              <div className="space-y-3">
                {upcomingChecks.map((check) => (
                  <div
                    key={check.id}
                    className={cn(
                      "bg-white border rounded-2xl shadow-sm p-4 cursor-pointer hover:shadow-md transition-shadow",
                      check.dueInDays === 0 ? 'border-error-100' : 'border-warning-300'
                    )}
                    onClick={() => navigate(`/entry/${check.id}`)}
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{check.description}</p>
                        <div className="flex items-center gap-1 text-xs mt-1">
                          <span className={cn("font-semibold", check.dueInDays === 0 ? 'text-error-600' : 'text-warning-600')}>
                            {check.dueInDays === 0 ? t('projects.due_today') : t('projects.due_in_days', { days: check.dueInDays })}
                          </span>
                          <span className="text-gray-400">·</span>
                          <span className="text-gray-500">{format(new Date(check.dueDate), "d MMMM yyyy", { locale: tr })}</span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end">
                        <MonoValue
                          value={check.amount}
                          prefix={check.currency === 'USD' ? '$' : check.currency === 'EUR' ? '€' : '₺'}
                          format
                          fractionDigits={2}
                          tone={check.dueInDays === 0 ? "error" : "default"}
                          size="md"
                        />
                        <span className="text-xs text-gray-500 mt-0.5">
                          {check.isGivenCheck ? t('projects.given_check') : t('projects.received_check')}
                        </span>
                        {check.dueInDays === 0 && (
                          <Button
                            size="sm"
                            className="mt-2 bg-brand-600 hover:bg-brand-700 h-7 text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCheckStatusClick(check);
                            }}
                          >
                            {t("projects.update_status")}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Complete / Leave project */}
          {project.status !== "completed" && (
            <div className="mx-4 mt-8 border-t border-gray-200 pt-4">
              {permissions.role === 'viewer' ? (
                <div>
                  <Button
                    variant="outline"
                    className="w-full border-error-600 text-error-600 hover:bg-error-50 hover:text-error-600"
                    onClick={() => setLeaveDialogOpen(true)}
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    {t("projects.detail.leave_project.title")}
                  </Button>
                  <p className="text-sm text-center text-gray-500 mt-2">
                    {t("projects.detail.leave_project.viewer_warning")}
                  </p>
                </div>
              ) : permissions.canComplete ? (
                <div>
                  <Button
                    variant="outline"
                    className="w-full border-error-600 text-error-600 hover:bg-error-50"
                    onClick={() => setCompleteDialogOpen(true)}
                  >
                    {t("projects.detail.complete_project.title")}
                  </Button>
                  <p className="text-sm text-center text-gray-500 mt-2">
                    {t("projects.detail.complete_project.warning_message")}
                  </p>
                </div>
              ) : permissions.role === 'editor' ? (
                <div>
                  <Button
                    variant="outline"
                    className="w-full border-error-600 text-error-600 hover:bg-error-50 hover:text-error-600"
                    onClick={() => setLeaveDialogOpen(true)}
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    {t("projects.detail.leave_project.title")}
                  </Button>
                  <p className="text-sm text-center text-gray-500 mt-2">
                    {t("projects.detail.leave_project.editor_warning")}
                  </p>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </main>

      <BottomNavigation />

      {/* Complete Project dialog */}
      <AlertDialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("projects.detail.complete_project.dialog_title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("projects.detail.complete_project.dialog_description")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCompleting}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleCompleteProject} disabled={isCompleting}>
              {isCompleting ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("projects.detail.complete_project.processing")}</>
              ) : t("projects.detail.complete_project.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Leave Project dialog */}
      <AlertDialog open={leaveDialogOpen} onOpenChange={setLeaveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("projects.detail.leave_project.dialog_title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("projects.detail.leave_project.dialog_description")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLeaving}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleLeaveProject}
              disabled={isLeaving}
              className="bg-error-600 text-white hover:bg-error-600/90"
            >
              {isLeaving ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("projects.detail.leave_project.leaving")}</>
              ) : (
                <><LogOut className="mr-2 h-4 w-4" />{t("projects.detail.leave_project.confirm")}</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Check Status Dialog */}
      {selectedCheck && (
        <CheckStatusDialog
          open={checkStatusDialogOpen}
          onOpenChange={setCheckStatusDialogOpen}
          isGivenCheck={selectedCheck.isGivenCheck}
          checkId={selectedCheck.id}
          checkAmount={selectedCheck.amount}
          checkDescription={selectedCheck.description}
          onStatusUpdate={handleCheckStatusUpdate}
        />
      )}
    </div>
  );
};

export default ProjectDetailPage;
